import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../lib/adminAuth";
import { ErrorBanner } from "../../components/admin/AdminUI";
import { Button, Card, Input, Label, MeasureRule } from "../../components/fulkra";

/**
 * Inlogscherm in de huisstijl: links het donkere merkvlak met de schaallat, rechts het
 * formulier op papier. Precies zoals het ontwerp het tekent (app/WerkgeverPortaal — Inloggen).
 *
 * Restyling, geen herbouw: dezelfde velden, dezelfde validatie, dezelfde foutafhandeling
 * en dezelfde data-testid's. Op een telefoon vervalt de linkerkolom — daar is de ruimte
 * te kostbaar voor een merkvlak.
 */

const schema = z.object({
  email: z.string().email("Ongeldig e-mailadres"),
  password: z.string().min(1, "Wachtwoord verplicht"),
});

const MEDIA_REGELS = [
  "@media (max-width: 860px) {",
  "  .fulkra-inlog { grid-template-columns: 1fr !important; }",
  "  .fulkra-inlog__merk { min-height: auto !important; padding: var(--ruimte-8) var(--ruimte-7) !important; }",
  "  .fulkra-inlog__merk h1 { font-size: var(--fs-h2) !important; }",
  "}",
].join("\n");

export default function LoginPage() {
  const { login, isAuthenticated, mfaPending, bootstrapping } = useAdminAuth();
  const [apiError, setApiError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  if (bootstrapping) return null;
  if (isAuthenticated) return <Navigate to={from} replace />;
  if (mfaPending) return <Navigate to="/admin/mfa" replace />;

  async function onSubmit(values) {
    setApiError(null);
    setSubmitting(true);
    try {
      const result = await login(values.email, values.password);
      if (result.mfa_required) {
        navigate("/admin/mfa", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (e) {
      setApiError(e);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fulkra fulkra-inlog"
      data-testid="login-page"
      style={{ minHeight: "100vh", display: "grid", gridTemplateColumns: "1fr 1fr" }}
    >
      <div
        className="fulkra-inlog__merk"
        style={{
          background: "var(--ink)", color: "var(--text-inverse)",
          padding: "var(--ruimte-12) var(--ruimte-10)",
          display: "flex", flexDirection: "column", justifyContent: "space-between", gap: "var(--ruimte-9)",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-kop)", fontWeight: "var(--fw-semi)", fontSize: "var(--fs-h2)",
            letterSpacing: "var(--ls-kop)",
          }}
        >
          Fulkra Works
        </span>
        <div>
          <h1 style={{ font: "var(--text-h1)", color: "var(--text-inverse)", letterSpacing: "var(--ls-kop)", maxWidth: 380 }}>
            Preventieprogramma voor staand en lopend werk
          </h1>
          <MeasureRule height={12} spacing={7} accent="rgba(247, 249, 250, .3)" style={{ marginTop: "var(--ruimte-8)", maxWidth: 380 }} />
        </div>
        <span style={{ fontSize: "var(--fs-xs)", color: "rgba(247, 249, 250, .5)" }}>
          Fulkra Works — onderdeel van Professional Podotherapie
        </span>
      </div>

      <div
        style={{
          background: "var(--surface-page)", display: "flex", alignItems: "center",
          justifyContent: "center", padding: "var(--ruimte-10)",
        }}
      >
        <div style={{ width: "100%", maxWidth: 380 }}>
          <Card title="Inloggen" subtitle="Toegang voor beheerders en aanmeters">
            <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ display: "grid", gap: "var(--ruimte-6)" }}>
              <ErrorBanner error={apiError} />

              <div style={{ display: "grid", gap: "var(--ruimte-3)" }}>
                <Label htmlFor="login-email" required>E-mailadres</Label>
                <Input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  data-testid="login-email"
                  placeholder="naam@bedrijf.nl"
                  invalid={Boolean(errors.email)}
                  {...register("email")}
                />
                {errors.email ? (
                  <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-alarm)" }} data-testid="login-email-error">
                    {errors.email.message}
                  </span>
                ) : null}
              </div>

              <div style={{ display: "grid", gap: "var(--ruimte-3)" }}>
                <Label htmlFor="login-password" required>Wachtwoord</Label>
                <Input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  data-testid="login-password"
                  placeholder="••••••••"
                  invalid={Boolean(errors.password)}
                  {...register("password")}
                />
                {errors.password ? (
                  <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-alarm)" }} data-testid="login-password-error">
                    {errors.password.message}
                  </span>
                ) : null}
              </div>

              <Button type="submit" size="touch" disabled={submitting} data-testid="btn-login-submit">
                {submitting ? "Bezig…" : "Inloggen"}
              </Button>

              <p style={{ fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
                Na inloggen vraagt de app om uw authenticatiecode van zes cijfers.
              </p>

              <Link
                to="/admin/wachtwoord-vergeten"
                data-testid="link-wachtwoord-vergeten"
                style={{ fontSize: "var(--fs-sm)", color: "var(--text-action)", textAlign: "center" }}
              >
                Wachtwoord vergeten?
              </Link>
            </form>
          </Card>
        </div>
      </div>

      <style>{MEDIA_REGELS}</style>
    </div>
  );
}
